import {
    decisionUrl,
    managerIdParameterName,
    workflowStatusParameterName,
    budgetCycleIdParameterName,
    cycleIdParameterName,
    reportsParameterName,
    employeeSourceIdParameterName,
    sidePanelParamaterName,
    employeeSortParamaterName,
    jobFilterParamaterName,
    departmentFilterParamaterName,
    promotionFilterParamaterName,
    employeeFilterParamaterName
} from 'nomst/Stores/Navigation/Constants//decisionPageUrl';
import { INavigationStore } from 'nomst/Stores/Navigation/navigation.store';
import { SidePanelListType, Views, WorkflowStatusListType, ReportsView, NavigationErrors, PageTabs } from 'typings/enums';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import * as singletons from 'utils/MobxDataManager/singletons';
import * as formatParse from 'common/format-parse';
import { IDecisionDispatcher } from 'typings/decision.dispatcher';
import * as actions from 'nomst/Actions/manager';
import { EmployeeSort } from 'nomst/Stores/Decision/Constants/employeeSort';
import { IEmployeeViewNavigationParameters } from 'typings/navigation';

export class DecisionNavigationStore {
    queryParameters: {[key: string]: string[]};
    private _setPageTab: (tab: PageTabs) => void;
    private readonly _decisionDispatcher: IDecisionDispatcher;
    private readonly _configuration: Configuration;
    private readonly _navigationStore: INavigationStore;

    private _cycleId: number;
    private _managerSourceIds: string[] | undefined;
    private _workflowStatus: WorkflowStatusListType;
    private _employeeSourceId: Nullable<string>;
    private _reportsView: ReportsView;
    private _budgetCycleId: Nullable<number>;
    private _showSidePanel: boolean;
    private _sortBy: EmployeeSort;
    private _shouldFullyNavigate: boolean;
    private _jobFilter: Nullable<string>;
    private _departmentFilter: Nullable<string>;
    private _promotionFilter: boolean;
    private _employeeFilter: Nullable<string>;

    constructor(
        navigationStore: INavigationStore,
        decisionDispatcher: IDecisionDispatcher,
        configuration: Configuration,
        setPageTab: (tab: PageTabs) => void) {
        this._setPageTab = setPageTab;
        this._decisionDispatcher = decisionDispatcher;
        this._configuration = configuration;
        this._navigationStore = navigationStore;
        this._cycleId = 0;
        this._managerSourceIds = [];
        this._workflowStatus = WorkflowStatusListType.Open;
        this._employeeSourceId = null;
        this._reportsView = ReportsView.DirectsOnly;
        this._budgetCycleId = 0;
        this._showSidePanel = true;
        this._sortBy = EmployeeSort.NameSortOption;
        this._shouldFullyNavigate = true;
        this._jobFilter = null;
        this._departmentFilter = null;
        this._promotionFilter = false;
        this._employeeFilter = null;
    }

    get setPageTab(): (tab: PageTabs) => void {
        return this._setPageTab;
    }

    async handleDecisionRoutes(): Promise<void> {
        const url = decisionUrl(location.pathname);
        if (!url) {
            return;
        }

        if (url.view === Views.OrgChart) {
            //// no special logic, so it has been handled directly using react router in the view.
            return;
        }

        await this.handleTeamDecisionPageRoute(location.search);
    }

    setNavigationParamaters(
        cycleId: number,
        managerSourceIds: string[]|undefined,
        workflowStatus: WorkflowStatusListType,
        budgetCycleId: Nullable<number>,
        reportsView: ReportsView,
        sortBy: EmployeeSort = EmployeeSort.NameSortOption,
        jobFilter: Nullable<string> = null,
        departmentFilter: Nullable<string> = null,
        promotionFilter: boolean = false,
        employeeFilter: Nullable<string> = null): void {
        this._cycleId = cycleId;
        this._managerSourceIds = managerSourceIds;
        this._workflowStatus = workflowStatus;
        this._budgetCycleId = budgetCycleId;
        this._reportsView = reportsView;
        this._sortBy = sortBy;
        this._jobFilter = jobFilter;
        this._departmentFilter = departmentFilter;
        this._promotionFilter = promotionFilter;
        this._employeeFilter = employeeFilter;
    }

    getAllParameters(url: string): IEmployeeViewNavigationParameters {
        this.queryParameters = formatParse.getJsonFromUrl(url);
        this._managerSourceIds = this.queryParameters[managerIdParameterName];
        this._cycleId = parseInt(formatParse.getFirstOnly(this.queryParameters[cycleIdParameterName]), 10);
        this._sortBy = formatParse.getFirstOnly(this.queryParameters[employeeSortParamaterName]) as EmployeeSort ?? EmployeeSort.NameSortOption;
        this._workflowStatus =
        formatParse.getFirstOnly(this.queryParameters[workflowStatusParameterName]) as WorkflowStatusListType;
        this._budgetCycleId =
        parseInt(formatParse.getFirstOnly(this.queryParameters[budgetCycleIdParameterName]), 10);
        // If not specified default to direct reports view as it's the use case for most of the users
        this._reportsView = formatParse.getFirstOnly(this.queryParameters[reportsParameterName]) as ReportsView ||
        ReportsView.DirectsOnly;
        this._employeeSourceId = formatParse.getFirstOnly(this.queryParameters[employeeSourceIdParameterName]);
        this._showSidePanel = formatParse.getFirstOnly(this.queryParameters[sidePanelParamaterName]) === 'true';
        this._jobFilter = formatParse.getFirstOnly(this.queryParameters[jobFilterParamaterName]);
        this._departmentFilter = formatParse.getFirstOnly(this.queryParameters[departmentFilterParamaterName]);
        this._promotionFilter = formatParse.getFirstOnly(this.queryParameters[promotionFilterParamaterName]) === 'true';
        this._employeeFilter = formatParse.getFirstOnly(this.queryParameters[employeeFilterParamaterName]);
        
        return {
            CycleId: this._cycleId, 
            ManagerSourceIds: this._managerSourceIds,
            WorkflowStatus: this._workflowStatus,
            ReportsView: this._reportsView,
            ShowSidePanel: this._showSidePanel,
            SortBy: this._sortBy, 
            PromotionFilter: this._promotionFilter,
            JobTitleFilter: this._jobFilter,
            DepartmentFilter: this._departmentFilter,
            SelectedEmployee: this._employeeFilter,
            QueryParameters:  this.queryParameters,
        };
    }

    setShouldFullyNavigate(value: boolean): void {
        this._shouldFullyNavigate = value;
    }

    async handleTeamDecisionPageRoute(url: string): Promise<void> {
        // when updating the url just to reflect current parameters do not head down
        // the full path below.
        // Just stop... navigate and listen   -Vanilla Navigation Ice
        if (!this._shouldFullyNavigate) {
            this.setShouldFullyNavigate(true);
            return;
        }
        this._decisionDispatcher.dispatchResetView();
        this._setPageTab(PageTabs.Teams);
        const queryParameters = this.getAllParameters(url).QueryParameters;
        this._decisionDispatcher.dispatchSetTeamView(this._reportsView);
        this._decisionDispatcher.dispatchSidePanelToggled(this._showSidePanel);
        this._decisionDispatcher.dispatchSetSelectedSortFormat(this._sortBy);

        this.sessionHealthCheck();

        // if the queryParameters object contains an empty string for a key then someone has come over here from MX
        // and we have no parameters whatsoever to go off of. In this case use the managers' employee id as the manager id 
        // the cycle in the configuration to nav to the direct teams view 
        if (Object.keys(queryParameters).filter(key => key === '').length) {
            return this._navigationStore.goToTeamsFromMx();
        }

        if (this._budgetCycleId) {
            await this.getManagersBasedOnOverBudget();
        } else {
            await this.getManagersBasedOnWorkflowStatus();
        }
        if (this._shouldFullyNavigate) {
            this._decisionDispatcher.dispatchJobAndDepartmentFilters(this._jobFilter, this._departmentFilter, true);
            this._decisionDispatcher.dispatchPromotionFilter(this._promotionFilter, true);
            this._decisionDispatcher.dispatchEmployeeSelection(this._employeeFilter);
        }
        this.setShouldFullyNavigate(true);
    }

    async getManagersBasedOnOverBudget(): Promise<void> {
        const sidePanelListType = SidePanelListType.OverBudget;
        if (!this._managerSourceIds?.length) {
            const managerSourceId = await this.getFirstManagerSourceIdByListAsync(
                sidePanelListType,
                this._budgetCycleId ?? 0);
            if (managerSourceId) {
                return this._navigationStore.navigateToOverBudgetManagersRoute(
                    this._cycleId,
                    [managerSourceId],
                    this._budgetCycleId ?? 0,
                    this._reportsView);
            }

            this._decisionDispatcher.dispatchSetTeamSelection(false);
        } else {
            this._decisionDispatcher.dispatchSetTeamSelection(true);
            await this._decisionDispatcher.dispatchSetOverBudgetManagerAsync(
                sidePanelListType,
                this._managerSourceIds,
                this._budgetCycleId ?? 0);
        }
    }

    async getManagersBasedOnWorkflowStatus(): Promise<void> {
        const sidePanelListType = SidePanelListType.WorkflowStatus;

        if (singletons.managerListSidePanelStore.isFirstLevelManager &&
        (!this._cycleId || singletons.cycleRules.isCurrentCyclePendingOrPreview)) {
            this.setShouldFullyNavigate(false);
            return this._navigationStore.navigateToEmptyManagerView();
        }

        this.buildWorkflowAfterNavigation();

        if (this._managerSourceIds?.length === 1 &&
        !singletons.managerListSidePanelStore.managerCardsContainer.
            isManagerInWorkflow(this._managerSourceIds[0], this._workflowStatus)) {
            await singletons.managerListSidePanelStore.fetchAllWorkflows();
            this._workflowStatus = singletons.managerListSidePanelStore.managerCardsContainer.
                findManagersWorkflow(this._managerSourceIds[0]);
        }

        if (!this._managerSourceIds?.length) {
            const managerSourceId = await this.getFirstManagerSourceIdByListAsync(
                sidePanelListType,
                this._workflowStatus);
            this._managerSourceIds = [managerSourceId ?? ''];
        }
        this._decisionDispatcher.dispatchSetTeamSelection(true);

        await this._decisionDispatcher.dispatchSetWorkflowStatusManagersAsync(
            sidePanelListType,
            this._managerSourceIds,
            this._workflowStatus);

        if (this._employeeSourceId) {
            return this._decisionDispatcher.dispatchEmployeeSelection(this._employeeSourceId);
        }
    }

    async buildWorkflowAfterNavigation(): Promise<void> {
        if (!singletons.managerListSidePanelStore.managerWorkflowSummaryLookups) {
            await singletons.managerListSidePanelStore.getManagerWorkflowSummaries();
        }

        if (singletons.managerListSidePanelStore.isFirstLevelManager) {
            this._workflowStatus = actions.getFirstLevelManagerWorkflowStatus();
        }

        if (this._workflowStatus) {
            await singletons.managerListSidePanelStore.fetchWorkflowManagerIds(this._workflowStatus);
        }

        if (!this._workflowStatus || !actions.doesWorkflowStatusExist(this._workflowStatus)) {
            // we have navigated to poorly. It's time to find our way back out
            // Find a status that currently has people in it and pull all the manager workflows
            // so we can find where the manager is
            await singletons.managerListSidePanelStore.fetchAllWorkflows();
            this._workflowStatus = actions.getFirstManagerWorkflowStatus();
        }
    }

    async getFirstManagerSourceIdByListAsync(
        sidePanelListType: SidePanelListType,
        managerListType: number | WorkflowStatusListType): Promise<Nullable<string>> {

        const manager  = await singletons.dataManagerFactory
            .getManagerDataManager()
            .getFirstManagerByListAsync(
                this._configuration.masterAccountId,
                this._configuration.cycleId,
                sidePanelListType,
                managerListType.toString());

        return manager ? manager.employeeSourceId : null;
    }

    // check to see if the user is going to have a bad experience based on current settings
    // and if so redirect the user to a help page
    sessionHealthCheck(): void {
        if (singletons.managerListSidePanelStore.hasNoRoles) {
            singletons.errorMessages.setErrorMessage(NavigationErrors.NoRoles);
            return this._navigationStore.navigateToErrorMessageView();
        }

        if (singletons.managerListSidePanelStore.isMissingManagerRole) {
            singletons.errorMessages.setErrorMessage(NavigationErrors.NoManagerRoles);
            return this._navigationStore.navigateToErrorMessageView();
        }
    }
}

export interface IDecisionNavigationStore extends DecisionNavigationStore {}